import React, { useContext, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { AppContext } from "../../../context/AppContext";
import CryptoFuturesCoins from "../CryptoFuturesCoins/CryptoFuturesCoins";
import { StyledPopover } from "./styles";

const POPOVER_CLOSE_DELAY_MS = 120;

const MyPopover = ({
  parentOption,
  icon,
  title,
  subOptions = [],
  onClick,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { selectedOption, updateSelectedOption } = useContext(AppContext);
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);
  const closeTimerRef = useRef(null);
  const suppressNextFocusOpenRef = useRef(false);
  const focusFirstMenuItemRef = useRef(false);
  const popoverId = useId();
  const label = parentOption?.text || title || "사이드바 메뉴";
  const hasSubOptions = subOptions.length > 0;

  const clearScheduledClose = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openPopover = () => {
    clearScheduledClose();
    setIsPopoverOpen(true);
  };

  const schedulePopoverClose = () => {
    clearScheduledClose();
    closeTimerRef.current = window.setTimeout(() => {
      setIsPopoverOpen(false);
      closeTimerRef.current = null;
    }, POPOVER_CLOSE_DELAY_MS);
  };

  useEffect(
    () => () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!isPopoverOpen || !focusFirstMenuItemRef.current) return;

    focusFirstMenuItemRef.current = false;
    popoverRef.current?.querySelector('[role="menuitem"]')?.focus();
  }, [isPopoverOpen]);

  const calculatePosition = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return { left: 66, top: 0 };

    return {
      left: rect.right + 10,
      top: Math.max(8, hasSubOptions ? rect.bottom - 72 : rect.top + 4),
    };
  };

  const handleEscape = (event) => {
    if (event.key !== "Escape") return;
    clearScheduledClose();
    setIsPopoverOpen(false);

    if (document.activeElement !== buttonRef.current) {
      suppressNextFocusOpenRef.current = true;
      buttonRef.current?.focus();
    }
  };

  const handleTriggerKeyDown = (event) => {
    if (
      hasSubOptions &&
      (event.key === "ArrowDown" ||
        event.key === "ArrowRight" ||
        event.key === "Enter" ||
        event.key === " ")
    ) {
      event.preventDefault();
      clearScheduledClose();
      if (isPopoverOpen) {
        popoverRef.current?.querySelector('[role="menuitem"]')?.focus();
      } else {
        focusFirstMenuItemRef.current = true;
        setIsPopoverOpen(true);
      }
      return;
    }

    handleEscape(event);
  };

  const handleMenuKeyDown = (event) => {
    if (event.key === "Escape") {
      handleEscape(event);
      return;
    }

    if (
      event.key !== "ArrowDown" &&
      event.key !== "ArrowUp" &&
      event.key !== "Home" &&
      event.key !== "End"
    ) {
      return;
    }

    const menuItems = Array.from(
      popoverRef.current?.querySelectorAll('[role="menuitem"]') || []
    );
    if (menuItems.length === 0) return;

    event.preventDefault();
    const activeIndex = menuItems.indexOf(document.activeElement);
    const direction = event.key === "ArrowDown" ? 1 : -1;
    const nextIndex = event.key === "Home"
      ? 0
      : event.key === "End"
        ? menuItems.length - 1
        : activeIndex < 0
          ? direction > 0
            ? 0
            : menuItems.length - 1
          : (activeIndex + direction + menuItems.length) % menuItems.length;
    menuItems[nextIndex]?.focus();
  };

  const handleTriggerClick = (event) => {
    clearScheduledClose();
    setIsPopoverOpen(false);
    onClick?.(event);
  };

  const handleSubOptionClick = (sidebarUrl) => {
    clearScheduledClose();
    setIsPopoverOpen(false);
    updateSelectedOption(sidebarUrl);
  };

  const handleTriggerFocus = () => {
    clearScheduledClose();
    if (suppressNextFocusOpenRef.current) {
      suppressNextFocusOpenRef.current = false;
      return;
    }
    setIsPopoverOpen(true);
  };

  const positionStyles = calculatePosition();
  const TriggerComponent = parentOption ? Link : "button";

  const popoverContent = isPopoverOpen ? (
    <StyledPopover
      ref={popoverRef}
      id={popoverId}
      role={hasSubOptions ? "menu" : "tooltip"}
      $hasSubOptions={hasSubOptions}
      style={{
        height: "auto",
        left: `${positionStyles.left}px`,
        top: `${positionStyles.top}px`,
        width: hasSubOptions ? "208px" : "auto",
      }}
      onMouseEnter={openPopover}
      onMouseLeave={schedulePopoverClose}
      onFocus={openPopover}
      onBlur={schedulePopoverClose}
      onKeyDown={handleMenuKeyDown}
    >
      {hasSubOptions ? (
        <div className="container-popover">
          <div className="popover-menu-content">
            <div className="title">{label}</div>
            {parentOption.text === "Crypto Futures" ? (
              <div className="crypto-futures-popover-content">
                <CryptoFuturesCoins />
              </div>
            ) : (
              subOptions.map((option, optionIndex) => (
                <Link
                  key={`${parentOption.text}-${option.text}-${optionIndex}`}
                  to={option.sidebarUrl}
                  className="option"
                  role="menuitem"
                  tabIndex={-1}
                  onClick={() => handleSubOptionClick(option.sidebarUrl)}
                >
                  {option.icon && (
                    <div className="icon">
                      <option.icon
                        className={
                          option.sidebarUrl === selectedOption ? "activated" : ""
                        }
                      />
                    </div>
                  )}
                  {option.text}
                </Link>
              ))
            )}
          </div>
        </div>
      ) : (
        <div className="simple-popover">
          {label}
          <span className="arrow arrow-left" aria-hidden="true" />
        </div>
      )}
    </StyledPopover>
  ) : null;

  return (
    <div className="relative" onMouseEnter={openPopover} onMouseLeave={schedulePopoverClose}>
      <TriggerComponent
        {...(parentOption
          ? { to: parentOption.sidebarUrl }
          : { type: "button" })}
        ref={buttonRef}
        className="link collapsed-sidebar-trigger"
        onClick={handleTriggerClick}
        onFocus={handleTriggerFocus}
        onBlur={schedulePopoverClose}
        onKeyDown={handleTriggerKeyDown}
        aria-label={label}
        aria-describedby={!hasSubOptions && isPopoverOpen ? popoverId : undefined}
        aria-controls={hasSubOptions ? popoverId : undefined}
        aria-expanded={hasSubOptions ? isPopoverOpen : undefined}
        aria-haspopup={hasSubOptions ? "menu" : undefined}
      >
        {parentOption?.icon ? (
          <parentOption.icon
            className={parentOption.sidebarUrl === selectedOption ? "activated" : ""}
          />
        ) : (
          icon
        )}
      </TriggerComponent>
      {popoverContent && typeof document !== "undefined"
        ? createPortal(popoverContent, document.body)
        : null}
    </div>
  );
};

export default MyPopover;
